/* eslint-disable max-lines-per-function */
import React, {
  useContext,
  useEffect, useMemo, useState,
} from 'react';
import clipboardCross from 'assets/clipboardCross.svg';
import VOPConfirmationModal from 'components/Payments/VOP/VOPConfirmationModal/VOPConfirmationModal';
import VOPStatusBadge from 'components/Payments/VOP/VOPStatusBadge';
import EmptyTable from 'components/Table/EmptyTable';
import VOPCheckTooltip from 'components/VOPCheckTooltip/VOPCheckTooltip';
import BeneficiaryInfoDrawer from 'domain/Transactions/BatchSummary/PaymentSummary/BeneficiaryInfoDrawer/BeneficiaryInfoDrawer';
import ActionDropDown from 'domain/Transactions/Dashboard/FxTradeTable/DisplayTable/ActionDropDown/ActionDropDown';
import NoOfApprovers from 'domain/Transactions/Dashboard/FxTradeTable/DisplayTable/NoOfApprovers/NoOfApprovers';
import PaymentInfoDrawer from 'domain/Transactions/PaymentInfoDrawer/PaymentInfoDrawer';
import useSwitchAccount from 'hooks/useSwitchAccount';
import PaymentsService from 'services/Payments/payments.service';
import { formatNumber } from 'utils/currency.helpers';
import formatIsoDate from 'utils/formatIsoDate';
import mapPaymentOrigin from 'utils/payments/mapPaymentOrigin';
import mapPaymentStatusDisplay from 'utils/payments/mapPaymentStatusDisplay';
import t from 'utils/translationHelper';

import { PaymentDto, PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { Box, Button } from '@alpha/ui-lib/ui/external';
import { Status } from '@alpha/ui-lib/ui/Status';
import { IStyledGenericTableProps, StyledGenericTable } from '@alpha/ui-lib/ui/table';
import { datadogLogs } from '@datadog/browser-logs';

import useStyles from '../index.styles';
import useOutstandingActionCache from '../useOutstandingActionCache';
import { OutstandingActionsContext } from '..';

const VerificationApprovals: React.FC = () => {
  const classes = useStyles();
  const { currentAccount } = useSwitchAccount();
  const CACHE_KEY = `${currentAccount.id}-pending-verifications`;
  const [pendingVerificationPayments, setPendingVerificationPayments] = useState<PaymentDto[]>([]);
  const [beneficiaryId, setBeneficiaryId] = useState<string>();
  const { saveResponse, getResponse } = useOutstandingActionCache();
  const [selectedPayment, setSelectedPayment] = useState<PaymentDto>();
  const { pendingVerificationsCount, setPendingVerificationsCount } = useContext(OutstandingActionsContext);
  const [readOnlyVerificationModalOpen, setReadOnlyVerificationModalOpen] = useState<PaymentDto | undefined>(undefined);
  const SHOW_LIMIT = 5;

  const loadPendingVerifications = async () => {
    try {
      const {
        total,
        records: pendingPayments,
      } = await PaymentsService.getPendingVerificationPayments();
      setPendingVerificationPayments(pendingPayments);
      if (setPendingVerificationsCount) { setPendingVerificationsCount(total); }
      saveResponse(CACHE_KEY, pendingPayments);
      saveResponse(`${CACHE_KEY}_count`, total);
    } catch (e) {
      datadogLogs.logger.error(e);
    }
  };

  useEffect(() => {
    try {
      const cachedPayments = getResponse<PaymentDto[]>(CACHE_KEY);
      const cachedCount = getResponse<number>(`${CACHE_KEY}_count`);
      if (cachedPayments) {
        setPendingVerificationPayments(cachedPayments as PaymentDto[]);
      }
      if (cachedCount) {
        if (setPendingVerificationsCount) { setPendingVerificationsCount(cachedCount); }
      }
    } finally {
      loadPendingVerifications();
    }
  }, []);

  const handleBeneficiaryButton = (beneId: string) => {
    setBeneficiaryId(beneId);
  };

  const columns: IStyledGenericTableProps['columns'] = [{
    header: t('id'),
    accessor: 'contractNumber',
    width: 100,
  },
  {
    header: t('source'),
    accessor: 'origin',
    align: 'left',
  },
  {
    header: t('beneficiary'),
    accessor: 'beneficiaryName',
    align: 'left',
  },
  {
    header: t('upload_date'),
    accessor: 'uploadedDate',
    align: 'left',
  },
  {
    header: t('amount'),
    accessor: 'instructedAmount',
    align: 'left',
  },
  {
    header: t('approvals'),
    accessor: 'approvals',
    align: 'left',
  },
  {
    header: t('status'),
    accessor: 'paymentStatus',
    align: 'left',
  }, {
    header: t('actions'),
    accessor: 'actions',
    align: 'left',
  }];

  const data = useMemo((): IStyledGenericTableProps['data'] => pendingVerificationPayments.slice(0, SHOW_LIMIT).map((payment) => ({
    ...payment,
    contractNumber: payment.contractNumber || '-',
    origin: mapPaymentOrigin(payment.origin) || '-',
    instructedAmount: (
      <span className={classes.amount}>
        {[payment.debitingCurrency, formatNumber(payment.amount, 2)].join(' ')}
      </span>),
    beneficiaryName: (
      <Button
        disableRipple
        className={classes.tableButton}
        style={{ minWidth: '150px', margin: '0px 0px 0px 0px' }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          if (handleBeneficiaryButton) { handleBeneficiaryButton(payment.beneficiaryId); }
        }}
      >
        <Box display="flex" alignItems="left" justifyContent="flex-start">
          {payment.vopRequired && (
          <VOPCheckTooltip
            vopCheck={payment.vopDetails?.status ?? PaymentVopMatchStatus.NOT_AVAILABLE}
            vopAccepted={payment.vopDetails?.vopApprovalAcknowledged}
          >
            <VOPStatusBadge
              status={payment.vopDetails?.status}
              onClick={(e) => {
                e.stopPropagation();
                setReadOnlyVerificationModalOpen(payment);
              }}
            />
          </VOPCheckTooltip>
          )}
          <span style={{ marginTop: '3px', marginLeft: '5px' }}>{payment.beneficiaryName || '-'}</span>
        </Box>
      </Button>),
    uploadedDate: formatIsoDate(payment.uploadedDate),
    approvals: (payment
      && (
        <NoOfApprovers
          uploadedBy={payment.uploadedBy}
          uploadedDate={payment.uploadedDate}
          noOfApprovals={payment.approvalInfo.approvals?.length}
          approvedBy={payment.approvalInfo.approvals || []}
          approvalsRequired={payment.approvalInfo.approvalsRequired}
        />
      )
    ) || '-',
    paymentStatus: (
      <Status
        className={classes.status}
        variant={mapPaymentStatusDisplay(payment.status).variant}
      >
        {t(mapPaymentStatusDisplay(payment.status).text) || '-'}

      </Status>),
    actions: (
      <ActionDropDown
        payment={payment}
        setDisplayMultiSelect={() => undefined}
        displayMultiSelect={false}
        setSelectedPayment={setSelectedPayment}
        handleReloadPaymentTable={loadPendingVerifications}
        pendingVerificationRow
      />
    ),
  }
  )), [pendingVerificationPayments]);


  return (
    <Box style={{
      overflow: 'auto',
      height: '230px',
    }}
    >
      {data.length > 0 ? (
        <>
          <StyledGenericTable
            className={classes.stickyHeaderTable}
            testId="pending-verifications-table"
            columns={columns}
            data={data}
          />

          {pendingVerificationsCount && pendingVerificationsCount > SHOW_LIMIT ? (
            <div className={classes.showAllMessage}>
              <span>
                {t('showing_top')}
                {' '}
                {SHOW_LIMIT}
                {' '}
                {t('items_please_click_to_view_all_items')}
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyTable
          title={t('no_pending_verifications')}
          subtitle={t('payment_awaiting_verification_display')}
          className={classes.emptyTable}
          icon={clipboardCross}
        />
      )}

      <BeneficiaryInfoDrawer
        paymentSummaryBeneficiaryId={beneficiaryId}
        onClose={() => setBeneficiaryId(undefined)}
        open={Boolean(beneficiaryId)}
        setPaymentSummaryBeneficiaryId={setBeneficiaryId}
      />
      <PaymentInfoDrawer
        paymentDrawerOpen={Boolean(selectedPayment)}
        setPaymentDrawerOpen={setSelectedPayment}
        selectedPayment={selectedPayment}
        sharedStyles={classes}
        testIds={
          {
            paymentDrawerBeneficiaryTable: 'payment-info-drawer-bene',
            paymentDrawerPaymentTable: 'payment-info-drawer-payment',
          }
        }
      />
      <VOPConfirmationModal
        readOnly
        open={!!readOnlyVerificationModalOpen}
        handleClose={() => setReadOnlyVerificationModalOpen(undefined)}
        payment={readOnlyVerificationModalOpen}
      />
    </Box>
  );
};
export default VerificationApprovals;
